import { useMemo } from "react";
import {
  LinearIssueStatusMap,
  useLinearIssueStatuses,
} from "./useLinearIssueStatuses";
import { isLinearIssue } from "routes/Staff/WorkflowConfig/linearTagHelpers";

/**
 * Hook to extract Linear tags from a mixed array and fetch their statuses.
 * Works with arrays of tag strings or tag objects with a name property.
 * Only queries Linear tags (ENG-XXX pattern), but can be used with mixed tag arrays.
 */
export function useLinearTags<T extends string | { name: string }>(
  tags: T[]
): {
  statusMap: LinearIssueStatusMap;
  loading: boolean;
  error: Error | undefined;
} {
  // Extract tag names from the array (handles both string and object formats)
  const tagNames = useMemo(() => {
    return tags.map((tag) => (typeof tag === "string" ? tag : tag.name));
  }, [tags]);

  // Extract only Linear identifiers
  const linearTagNames = useMemo(() => {
    return tagNames.filter((tagName) => isLinearIssue(tagName));
  }, [tagNames]);

  // Use the existing hook to fetch Linear issue statuses
  const { statusMap, loading, error } = useLinearIssueStatuses(linearTagNames);

  return {
    statusMap,
    loading,
    error,
  };
}
